import { notify, RevealButton } from '@affine/component';
import {
  AuthActionStack,
  AuthContainer,
  AuthContent,
  AuthHeader,
  AuthInput,
} from '@affine/component/auth-components';
import { Button } from '@affine/component/ui/button';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  type AccessGateMode,
  AuthService,
  CaptchaService,
} from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { UserFriendlyError } from '@affine/error';
import { Trans, useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AuthJourneyProgress } from '../auth-journey';
import { type LegalConsentHandle, LegalConsentSection } from '../consent';
import type { SignInState } from '.';
import { Back } from './back';
// Captcha component removed — PoW is triggered via useEffect on mount instead,
// to avoid the infinite revalidate loop that Captcha's useEffect caused.
import * as style from './style.css';

const emailRegex =
  /^(?:(?:[^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(?:(?:\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|((?:[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function validateEmail(email: string) {
  return emailRegex.test(email.trim());
}

/**
 * Two-phase Magic Link / OTP flow:
 *
 * Phase 1 (email entry): Email input (pre-filled if carried from sign-in page)
 *   + "Send code" button. User can always edit the email before sending.
 *
 * Phase 2 (OTP verification): After code is sent, shows OTP input
 *   + "Continue" button + "Resend" button.
 */
export const SignInWithEmailStep = ({
  state,
  changeState,
  onAuthenticated,
}: {
  state: SignInState;
  changeState: Dispatch<SetStateAction<SignInState>>;
  onAuthenticated?: (status: AuthSessionStatus) => void;
}) => {
  // Local email state — pre-filled from sign-in page, always editable
  const [email, setEmail] = useState(state.email ?? '');
  const [emailError, setEmailError] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [hasCapturedAccessCode, setHasCapturedAccessCode] = useState(false);
  const [accessMode, setAccessMode] = useState<AccessGateMode>();

  // sl-53cw.1.2: Unified consent component (registration variant)
  const [termsAccepted, setTermsAccepted] = useState(false);
  const consentRef = useRef<LegalConsentHandle>(null);

  // Phase tracking: false = email entry, true = OTP verification
  const [codeSent, setCodeSent] = useState(false);

  const [resendCountDown, setResendCountDown] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | undefined>();

  const t = useI18n();
  const authService = useService(AuthService);
  const captchaService = useService(CaptchaService);

  const verifyToken = useLiveData(captchaService.verifyToken$);
  const needCaptcha = useLiveData(captchaService.needCaptcha$);

  const loginStatus = useLiveData(authService.session.status$);
  const isPasswordRecovery = state.intent === 'recovery';
  const isRegistration =
    state.intent === 'registration' || state.intent === 'invite';
  const invalidEmailCodeMessage = isRegistration
    ? t['com.streamlain.auth.registration.email-code.invalid']()
    : t['com.streamlain.auth.sign-in.email-code.invalid']();

  useEffect(() => {
    onAuthenticated?.(loginStatus);
  }, [loginStatus, onAuthenticated]);

  useEffect(() => {
    authService.captureAccessCodeFromLocation();
    setHasCapturedAccessCode(authService.hasAccessCode());
    void authService
      .getAccessMode()
      .then(setAccessMode)
      .catch(() => {
        // Registration stays fail-closed until the public mode endpoint recovers.
      });
  }, [authService]);

  useEffect(() => {
    const timer = setInterval(() => {
      setResendCountDown(c => Math.max(c - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Trigger ALTCHA PoW challenge on mount (once).
  // The solution lands in captchaService.verifyToken$ which enables the send button.
  useEffect(() => {
    captchaService.revalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendEmail = useAsyncCallback(async () => {
    if (!validateEmail(email)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);

    if (!isPasswordRecovery && !termsAccepted) {
      consentRef.current?.scrollAndShake();
      return;
    }

    if (isSending || (!verifyToken && needCaptcha)) return;
    setIsSending(true);
    try {
      setResendCountDown(60);
      // Read token directly from LiveData — the closure-captured verifyToken
      // can be stale if revalidate() ran between render and click.
      const currentToken = captchaService.verifyToken$.value ?? verifyToken;
      captchaService.revalidate();
      if (isPasswordRecovery) {
        await authService.requestPasswordRecovery(email.trim(), currentToken!);
      } else {
        const admissionId = isRegistration
          ? await authService.createAccessAdmission(email.trim(), 'magic-link')
          : undefined;
        await authService.sendEmailMagicLink(
          email.trim(),
          currentToken,
          undefined,
          state.redirectUrl,
          state.intent === 'recovery' ? undefined : state.intent,
          admissionId
        );
      }
      setCodeSent(true);
      changeState(prev => ({ ...prev, emailCodeSent: true }));
    } catch (err) {
      console.error(err);
      const error = UserFriendlyError.fromAny(err);
      notify.error({
        title: t['com.streamlain.auth.toast.title.failed'](),
        message: error.is('EMAIL_SERVICE_NOT_CONFIGURED')
          ? t['error.EMAIL_SERVICE_NOT_CONFIGURED']()
          : t[`error.${error.name}`](error.data),
      });
    }
    setIsSending(false);
  }, [
    authService,
    changeState,
    captchaService,
    email,
    isSending,
    isPasswordRecovery,
    needCaptcha,
    state.redirectUrl,
    state.intent,
    termsAccepted,
    verifyToken,
    t,
    isRegistration,
  ]);

  const onSignInWithPasswordClick = useCallback(() => {
    changeState(prev => ({ ...prev, step: 'signIn' }));
  }, [changeState]);

  const onEmailChanged = useCallback((value: string) => {
    setEmail(value);
    setEmailError(false);
  }, []);

  const onAccessCodeChanged = useCallback(
    (value: string) => {
      setAccessCode(value);
      authService.setAccessCode(value);
      setHasCapturedAccessCode(authService.hasAccessCode());
    },
    [authService]
  );

  const onOtpChanged = useCallback((value: string) => {
    setOtp(value);
    setOtpError(undefined);
  }, []);

  const onContinue = useAsyncCallback(async () => {
    if (isVerifying) return;

    if (otp.length !== 6 || !/[0-9]{6}/.test(otp)) {
      setOtpError(invalidEmailCodeMessage);
      return;
    }

    setIsVerifying(true);

    try {
      const result = await authService.signInMagicLink(email, otp, false);
      if (result.requiresTwoFactor) {
        changeState(prev => ({
          ...prev,
          email,
          step: 'signInWithTotp',
          preferredTwoFactorMethod: result.preferredMethod,
        }));
      }
    } catch (e) {
      const error = UserFriendlyError.fromAny(e);
      notify.error({
        title: t['com.streamlain.auth.toast.title.failed'](),
        message: error.is('TOO_MANY_REQUEST')
          ? t['error.TOO_MANY_REQUEST']()
          : invalidEmailCodeMessage,
      });
      setOtpError(invalidEmailCodeMessage);
    } finally {
      setIsVerifying(false);
    }
  }, [
    authService,
    changeState,
    email,
    invalidEmailCodeMessage,
    isVerifying,
    otp,
    t,
  ]);

  // PoW preparation gates submission, but is not a user-triggered send state.
  const isCaptchaPending = !verifyToken && needCaptcha;
  const canSendEmail =
    validateEmail(email) &&
    (isPasswordRecovery || termsAccepted) &&
    (!isRegistration ||
      accessMode === 'open' ||
      (accessMode === 'gated' && hasCapturedAccessCode)) &&
    !isCaptchaPending &&
    !isSending;

  // Phase 1: Email entry — user enters/confirms email before code is sent
  if (!codeSent) {
    return (
      <AuthContainer>
        <AuthHeader
          className={style.signInHeader}
          title={
            isPasswordRecovery
              ? t['com.streamlain.auth.reset.password']()
              : isRegistration
                ? t['com.streamlain.auth.registration.email-code.title']()
                : t['com.streamlain.auth.sign-in.email-code.title']()
          }
          subTitle={
            isPasswordRecovery
              ? t['com.streamlain.auth.reset.password.message']()
              : isRegistration
                ? t['com.streamlain.auth.registration.email-code.subtitle']()
                : t['com.streamlain.auth.sign-in.email-code.subtitle']()
          }
        />
        <AuthContent>
          <AuthInput
            className={style.authInput}
            label={t['com.streamlain.settings.email']()}
            placeholder={t['com.streamlain.auth.sign.email.placeholder']()}
            value={email}
            onChange={onEmailChanged}
            error={emailError}
            errorHint={
              emailError ? t['com.streamlain.auth.sign.email.error']() : ''
            }
            onEnter={sendEmail}
          />

          {isRegistration && accessMode === 'gated' && (
            <AuthInput
              className={style.authInput}
              label={t['com.streamlain.auth.access-code.label']()}
              placeholder={
                hasCapturedAccessCode && accessCode.length === 0
                  ? t['com.streamlain.auth.access-code.captured']()
                  : t['com.streamlain.auth.access-code.placeholder']()
              }
              value={accessCode}
              onChange={onAccessCodeChanged}
              onEnter={sendEmail}
              autoComplete="off"
            />
          )}

          {!isPasswordRecovery && (
            <LegalConsentSection
              ref={consentRef}
              variant="registration"
              consentGiven={termsAccepted}
              onConsentChange={setTermsAccepted}
            />
          )}

          <AuthActionStack
            back={<Back changeState={changeState} />}
            progress={<AuthJourneyProgress />}
          >
            <RevealButton
              data-testid="send-code-button"
              label={
                isPasswordRecovery
                  ? t['com.streamlain.auth.send.reset.password.link']()
                  : isRegistration
                    ? t['com.streamlain.auth.registration.email-code.send']()
                    : t['com.streamlain.auth.sign-in.email-code.send']()
              }
              loading={isSending}
              disabled={!canSendEmail}
              onClick={sendEmail}
            />
          </AuthActionStack>
        </AuthContent>
      </AuthContainer>
    );
  }

  if (isPasswordRecovery) {
    return (
      <AuthContainer>
        <AuthHeader
          className={style.signInHeader}
          title={t['com.streamlain.auth.reset.password']()}
          subTitle={t['com.streamlain.auth.password-recovery.sent']()}
        />
        <AuthContent>
          <p>{t['com.streamlain.auth.password-recovery.sent']()}</p>
          <AuthActionStack
            back={<Back changeState={changeState} />}
            progress={<AuthJourneyProgress />}
          >
            <Button
              disabled={resendCountDown > 0 || isSending}
              variant="primary"
              onClick={sendEmail}
            >
              {resendCountDown <= 0
                ? t['com.streamlain.auth.send.reset.password.link']()
                : t['com.streamlain.auth.sign.auth.code.resend.hint']({
                    second: String(resendCountDown),
                  })}
            </Button>
          </AuthActionStack>
        </AuthContent>
      </AuthContainer>
    );
  }

  // Phase 2: OTP verification — code has been sent, user enters 6-digit code
  return (
    <AuthContainer>
      <AuthHeader
        className={style.signInHeader}
        title={
          isRegistration
            ? t['com.streamlain.auth.registration.email-code.title']()
            : t['com.streamlain.auth.sign-in.email-code.title']()
        }
        subTitle={
          isRegistration
            ? t['com.streamlain.auth.registration.email-code.subtitle']()
            : t['com.streamlain.auth.sign-in.email-code.subtitle']()
        }
      />
      <AuthContent>
        <p>
          {isRegistration ? (
            <Trans
              i18nKey="com.streamlain.auth.registration.email-code.hint"
              values={{ email }}
              components={{ a: <a href={`mailto:${email}`} /> }}
            />
          ) : (
            <Trans
              i18nKey="com.streamlain.auth.sign-in.email-code.hint"
              values={{ email }}
              components={{ a: <a href={`mailto:${email}`} /> }}
            />
          )}
        </p>

        <AuthInput
          placeholder={
            isRegistration
              ? t['com.streamlain.auth.registration.email-code.label']()
              : t['com.streamlain.auth.sign-in.email-code.label']()
          }
          onChange={onOtpChanged}
          error={!!otpError}
          errorHint={otpError}
          onEnter={onContinue}
          type="text"
          required={true}
          maxLength={6}
        />

        <div className={style.authMessage}>
          {t['com.streamlain.auth.sign.auth.code.message']()}
          &nbsp;
          <Trans
            i18nKey="com.streamlain.auth.sign.auth.code.message.password"
            components={{
              1: (
                <span
                  className="link"
                  data-testid="sign-in-with-password"
                  onClick={onSignInWithPasswordClick}
                />
              ),
            }}
          />
        </div>

        <AuthActionStack
          back={<Back changeState={changeState} />}
          progress={<AuthJourneyProgress />}
        >
          <RevealButton
            data-testid="continue-code-button"
            label={
              isRegistration
                ? t['com.streamlain.auth.registration.email-code.continue']()
                : t['com.streamlain.auth.sign-in.email-code.continue']()
            }
            onClick={onContinue}
            disabled={!!otpError || isVerifying}
            loading={isVerifying}
          />

          <Button
            disabled={resendCountDown > 0}
            variant="plain"
            onClick={sendEmail}
            style={{ padding: '4px' }}
          >
            {resendCountDown <= 0 ? (
              isRegistration ? (
                t['com.streamlain.auth.registration.email-code.resend']()
              ) : (
                t['com.streamlain.auth.sign-in.email-code.resend']()
              )
            ) : (
              <Trans
                i18nKey="com.streamlain.auth.sign.auth.code.resend.hint"
                values={{ second: resendCountDown }}
              />
            )}
          </Button>
        </AuthActionStack>
      </AuthContent>
    </AuthContainer>
  );
};
